import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { tokenStorage } from '../api/adminApiClient'
import { getMe, login as loginService, logout as logoutService } from '../services/authService'
import type { AdminMe, AdminLoginRequest } from '../types'

interface AdminAuthContextValue {
  me: AdminMe | null
  isLoading: boolean
  login: (credentials: AdminLoginRequest) => Promise<void>
  logout: () => Promise<void>
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null)

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<AdminMe | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const token = tokenStorage.getAccess()
    if (!token) {
      setIsLoading(false)
      return
    }
    getMe()
      .then(setMe)
      .catch(() => {
        tokenStorage.clear()
      })
      .finally(() => setIsLoading(false))
  }, [])

  async function login(credentials: AdminLoginRequest) {
    await loginService(credentials)
    const user = await getMe()
    setMe(user)
  }

  async function logout() {
    await logoutService()
    setMe(null)
  }

  return (
    <AdminAuthContext.Provider value={{ me, isLoading, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider')
  return ctx
}
